import { CheckIcon, CopyIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import invariant from 'tiny-invariant'
import z from 'zod'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './copy-button.css.ts'

export const CopyButtonSideSchema = z.enum(['top', 'right', 'bottom', 'left'])
export type CopyButtonSide = z.infer<typeof CopyButtonSideSchema>

export const CopyButtonSizeSchema = z.enum(['small', 'medium', 'large', 'xlarge'])
export type CopyButtonSize = z.infer<typeof CopyButtonSizeSchema>

const SIZE_TO_PIXELS_MAP: Record<CopyButtonSize, number> = {
  small: 12,
  medium: 16,
  large: 20,
  xlarge: 24,
}

type CopyButtonProps = {
  copyText: string
  tooltipText?: string
  side?: CopyButtonSide
  label: string
  className?: string
  size?: CopyButtonSize
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  copyText,
  tooltipText,
  label,
  className,
  side = 'top',
  size = 'small',
}) => {
  const sizeInPixels = SIZE_TO_PIXELS_MAP[size]
  invariant(sizeInPixels)

  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (copied) {
      timeout = setTimeout(() => {
        setCopied(false)
      }, 1600)
    }
    return () => clearTimeout(timeout)
  }, [copied])

  const handleCopy = () => {
    navigator.clipboard.writeText(copyText)
    setCopied(true)
  }

  if (tooltipText) {
    return (
      // gives the button width or else absolute placed icons give it a width of 0
      <div style={{ width: `${sizeInPixels}px`, height: `${sizeInPixels}px` }}>
        <TooltipWithHover
          trigger={
            <button
              className={classNames(styles.copyButton, className)}
              onClick={handleCopy}
              aria-label={label}
              style={{ width: sizeInPixels, height: sizeInPixels }}
            >
              <CopyIcon
                className={classNames(styles.icon, styles.copyIcon, { [styles.hidden]: copied })}
                size={sizeInPixels}
                weight="light"
              />
              <CheckIcon
                className={classNames(styles.icon, styles.checkIcon, { [styles.visible]: copied })}
                data-testid="check-icon"
                size={sizeInPixels}
                weight="regular"
              />
            </button>
          }
          side={side}
        >
          {tooltipText}
        </TooltipWithHover>
      </div>
    )
  }

  return (
    <button
      className={classNames(styles.copyButton, className)}
      onClick={handleCopy}
      aria-label={label}
      style={{ width: sizeInPixels, height: sizeInPixels }}
    >
      <CopyIcon
        className={classNames(styles.icon, styles.copyIcon, { [styles.hidden]: copied })}
        size={sizeInPixels}
        weight="light"
      />
      <CheckIcon
        className={classNames(styles.icon, styles.checkIcon, { [styles.visible]: copied })}
        data-testid="check-icon"
        size={sizeInPixels}
        weight="regular"
      />
    </button>
  )
}
